import {
  Checkbox,
  CheckboxProps,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
} from "@mui/material";
import { CHECKBOX_SIZE } from "core/consts";
import { getAriaDescribedByValue, getKey } from "core/model/utils/strings";
import { isPrintMode } from "core/model/utils/urls";
import { CUSTOM_BLACK, WHITE } from "ds_legacy/materials/colors";
import {
  FONT_SIZE_14,
  FONT_WEIGHT_SEMI_BOLD,
} from "ds_legacy/materials/typography";
import React, { CSSProperties, Fragment, ReactNode } from "react";
import {
  FormElementRenderProps,
  FormWatcher,
  isValid,
} from "react-forms-state/src";
import { useTranslations } from "translations";
import { getCheckboxStyle } from "../CheckboxInputField";
import { getHelperText } from "../Validation";

export type CheckboxOptionV2 = {
  label: string;
  subForm?: ReactNode;
  subFormStyle?: CSSProperties;
  value: string;
};

export type CheckboxGroupV2Props = {
  elementName: string;
  flatModel?: boolean;
  formControlLabelSx?: CSSProperties;
  formControlSx?: CSSProperties;
  formLabelSx?: CSSProperties;
  label: string;
  options: CheckboxOptionV2[];
  required?: boolean;
  sideMutation?: (
    value: (string | number)[],
    mutateElement: (v: any, name: string) => void,
  ) => void;
};

export default function CheckboxGroupV2({
  elementName,
  flatModel,
  formControlLabelSx,
  formControlSx,
  formLabelSx,
  label,
  options,
  required,
  sideMutation,
}: CheckboxGroupV2Props) {
  const translations = useTranslations();
  const isPrint = isPrintMode();

  return (
    <FormElementRenderProps
      elementName={elementName}
      flatModel={flatModel}
      sideMutation={sideMutation}
    >
      {({ onChange, validation, value }) => {
        const legendId = `${elementName}-checkbox-group-legend`;
        const errorTextId = `${elementName}_error_text_id`;
        const hasError = !isValid(validation);
        const currentValue: string[] = Array.isArray(value) ? value : [];
        const errorMessage = getHelperText({
          hasCustomValidation: true,
          translations,
          validation,
        });

        const handleCheckboxChange = (
          event: React.ChangeEvent<HTMLInputElement>,
        ) => {
          const { checked, value } = event.target;

          if (checked) {
            onChange([...currentValue, value]);
          } else {
            onChange(currentValue.filter((v) => v !== value));
          }
        };

        return (
          <FormControl
            component="fieldset"
            error={!isValid(validation)}
            required={required}
            sx={{ ...formControlSx }}
            variant="standard"
          >
            <FormLabel
              component="legend"
              id={legendId}
              sx={{
                color: hasError ? "" : CUSTOM_BLACK,
                fontWeight: FONT_WEIGHT_SEMI_BOLD,
                ...formLabelSx,
              }}
            >
              {label}
            </FormLabel>
            <FormGroup
              aria-labelledby={`${legendId} ${
                hasError ? errorTextId : ""
              }`.trim()}
            >
              {options.map((option, index) => (
                <Fragment key={getKey(option.value, index)}>
                  <FormControlLabel
                    key={option.value}
                    control={
                      <Checkbox
                        checked={currentValue.includes(option.value)}
                        color="primary"
                        inputProps={
                          {
                            "aria-describedby": getAriaDescribedByValue([
                              hasError && errorTextId,
                            ]),
                            "aria-invalid": hasError ? true : undefined,
                            ["data-testid"]: `checkbox-${elementName}-${option.value}`,
                          } as CheckboxProps["inputProps"]
                        }
                        name={elementName}
                        sx={{
                          "& .MuiSvgIcon-root": {
                            backgroundColor: WHITE,
                          },
                          fontSize: FONT_SIZE_14,
                          ...getCheckboxStyle({
                            checkboxSize: CHECKBOX_SIZE.SMALL,
                            isGroup: true,
                            print: isPrint,
                            validation,
                          }),
                        }}
                        value={option.value}
                        onChange={handleCheckboxChange}
                      />
                    }
                    label={option.label}
                    sx={{
                      fontSize: FONT_SIZE_14,
                      ...formControlLabelSx,
                    }}
                  />
                  {option.subForm && (
                    <FormWatcher
                      key={getKey("subForm", option.value, index)}
                      watchPath={elementName}
                    >
                      {({ watchedValue }) => {
                        const isSelected = (watchedValue || []).includes(
                          option.value,
                        );
                        return (
                          <div
                            style={{
                              display: isSelected ? "block" : "none",
                              ...option.subFormStyle,
                            }}
                          >
                            {option.subForm}
                          </div>
                        );
                      }}
                    </FormWatcher>
                  )}
                </Fragment>
              ))}
            </FormGroup>
            {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
          </FormControl>
        );
      }}
    </FormElementRenderProps>
  );
}
